import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { llmStoreService } from '@llm/service/llm_store.service';
import { environment } from '@env/environment';
import { DataService } from '@serv/data.service';
import { Tools_aiConsoleService } from "@llm/service/tools_ai-console.service";
import { NavigatorDeviceDetectorService } from '@llm/service/navigator_device_detector.service';
import { Router, NavigationEnd } from '@angular/router';
import { LogEntry, LogContext, BrowserInfo, SessionInfo, PerformanceMetrics } from '@llm/interfaces/log-entry.interface';

@Injectable({
    providedIn: 'root'
})
export class LlmLoggingService {
    private httpBackend = inject(HttpBackend);
    private http = inject(HttpClient);
    private llmStore = inject(llmStoreService);
    private dataService = inject(DataService);
    private consoleService = inject(Tools_aiConsoleService);
    private deviceDetector = inject(NavigatorDeviceDetectorService);
    private router = inject(Router);

    private readonly backendLogUrl = environment.mkgoURL + '/api/v1/llm-logs';

    // Cola de logs para batch processing
    private logQueue: LogEntry[] = [];
    private readonly BATCH_SIZE = 10;
    private readonly BATCH_INTERVAL = 5000;
    private batchTimer?: any;
    private isSendingError = false;

    // Información de sesión
    private sessionInfo: SessionInfo = {
        sessionId: this.generateSessionId(),
        sessionStart: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        pageVisits: 0,
        currentRoute: '',
        previousRoute: ''
    };

    constructor() {
        //this.http = new HttpClient(this.httpBackend);

        this.consoleService.log('LlmLoggingService inicializado.', 'DEBUG');
        this.initializeSessionTracking();
        this.startBatchProcessor();
    }

    /**
     * Log con nivel INFO
     */
    info(message: string, context?: any, component?: string, method?: string): void {
        const logEntry = this.createLogEntry('INFO', message, context, undefined, component, method);
        this.queueLog(logEntry);
    }

    /**
     * Log con nivel WARN
     */
    warn(message: string, context?: any, component?: string, method?: string): void {
        const logEntry = this.createLogEntry('WARN', message, context, undefined, component, method);
        this.queueLog(logEntry);
    }

    /**
     * Log con nivel ERROR
     */
    error(message: string, error?: any, context?: any, component?: string, method?: string): void {
        if (this.isSendingError) {
            console.error('CRITICAL: Error loop detected within LlmLoggingService.', message);
            return;
        }
        const enrichedContext = this.enrichErrorContext(error, context);
        const stackTrace = this.extractStackTrace(error);
        const logEntry = this.createLogEntry('ERROR', message, enrichedContext, stackTrace, component, method);

        this.sendLogToServer(logEntry);
    }

    /**
     * Log con nivel DEBUG
     */
    debug(message: string, context?: any, component?: string, method?: string): void {
        if (!environment.production) {
            const logEntry = this.createLogEntry('DEBUG', message, context, undefined, component, method);
            this.queueLog(logEntry);
        }
    }

    /**
     * Crea un objeto LogEntry con toda la información disponible
     */
    private createLogEntry(
        level: LogEntry['level'],
        message: string,
        context?: any,
        stackTrace?: string,
        component?: string,
        method?: string
    ): LogEntry {
        const companyId = this.dataService.company$.getValue()?.id || 'unknown';
        const userId = this.dataService.user?.id || 'unknown';

        const llmState = this.captureLlmState();
        const browserInfo = this.getBrowserInfo();
        const performance = this.getPerformanceMetrics();
        const enrichedContext: LogContext = {
            ...context,
            appState: {
                llm: llmState,
                route: this.router.url,
                ...context?.appState
            },
            url: window.location.href,
            userAgent: navigator.userAgent
        };

        return {
            level,
            timestamp: new Date().toISOString(),
            message,
            context: enrichedContext,
            stackTrace,
            errorType: context?.errorType,
            component,
            method,
            companyId,
            userId,
            environment: environment.production ? 'production' : 'development',
            browser: browserInfo,
            session: { ...this.sessionInfo },
            performance
        };
    }

    /**
     * Captura el estado actual del LLM Store
     */
    private captureLlmState(): Record<string, any> {
        try {
            return {
                isRecording: this.llmStore.isRecording(),
                isSpeaking: this.llmStore.isSpeaking(),
                isProcessingAudio: this.llmStore.isProcessingAudio(),
                isProcessingText: this.llmStore.isProcessingText(),
                agentExtracted: this.llmStore.agentExtracted(),
                isShowChat: this.llmStore.isShowChat(),
                recordingStatus: this.llmStore.recordingStatus(),
                isUserTypingText: this.llmStore.isUserTypingText()
            };
        } catch (e) {
            return { error: 'Failed to capture LLM state' };
        }
    }

    /**
     * Obtiene información del navegador
     */
    private getBrowserInfo(): BrowserInfo {
        return {
            name: this.deviceDetector.getBrowserName(),
            version: this.deviceDetector.getBrowserVersion(),
            os: this.deviceDetector.getOS(),
            platform: navigator.platform,
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenResolution: `${screen.width}x${screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            cookiesEnabled: navigator.cookieEnabled,
            onlineStatus: navigator.onLine
        };
    }

    /**
     * Obtiene métricas de performance
     */
    private getPerformanceMetrics(): PerformanceMetrics | undefined {
        if (!performance || !performance.timing) return undefined;

        const timing = performance.timing;
        const navigation: PerformanceMetrics = {
            navigationTiming: {
                loadTime: timing.loadEventEnd - timing.navigationStart,
                domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
                connectTime: timing.connectEnd - timing.connectStart,
                requestTime: timing.responseStart - timing.requestStart,
                responseTime: timing.responseEnd - timing.responseStart
            }
        };

        if ((performance as any).memory) {
            navigation.memory = {
                usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
                totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
                jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
            };
        }

        return navigation;
    }

    /**
     * Enriquece el contexto con información adicional del error
     */
    private enrichErrorContext(error: any, existingContext?: any): any {
        const context = { ...existingContext };

        if (!error) return context;

        if (error.status !== undefined) {
            context.http = {
                status: error.status,
                statusText: error.statusText,
                url: error.url,
                method: error.method,
                body: error.error
            };
        }

        if (error.ngDebugContext) {
            context.angular = {
                componentName: error.ngDebugContext.component?.constructor?.name,
                view: error.ngDebugContext.view?.def?.nodes
                    ?.map((n: any) => n?.element?.name)
                    .filter(Boolean)
                    .join(', '),
                renderNodeIndex: error.ngDebugContext.renderNodeIndex
            };
        }

        return context;
    }

    /**
     * Extrae el stack trace de forma segura
     */
    private extractStackTrace(error?: any): string | undefined {
        if (!error) return undefined;

        if (error.stack) return error.stack;
        if (error.rejection?.stack) return error.rejection.stack;

        try {
            return (new Error()).stack;
        } catch (e) {
            return undefined;
        }
    }

    /**
     * Añade un log a la cola para procesamiento por lotes
     */
    private queueLog(logEntry: LogEntry): void {
        this.logQueue.push(logEntry);

        if (this.logQueue.length >= this.BATCH_SIZE) {
            this.flushLogs();
        }
    }

    /**
     * Envía todos los logs en la cola
     */
    private async flushLogs(): Promise<void> {
        if (this.logQueue.length === 0) return;

        const logs = [...this.logQueue];
        this.logQueue = [];

        try {
            const options = await firstValueFrom(this.dataService.httpOptions(true));

            const headers = await firstValueFrom(this.dataService.httpOptions(true));
            await firstValueFrom(
                this.http.post(this.backendLogUrl + '/batch', { logs }, options)
            );

            this.consoleService.debug(
                `Batch de ${logs.length} logs enviado al backend`,
                null,
                '$LlmLoggingService $flushLogs'
            );
        } catch (err) {
            this.consoleService.error(
                `Error al enviar batch de logs al backend`,
                err,
                '$LlmLoggingService $flushLogs'
            );

            // Reintenta los logs fallidos
            // this.logQueue.unshift(...logsToSend);
        }
    }

    /**
     * Envía un log individual al servidor (usado para errores críticos)
     */
    private async sendLogToServer(logs: LogEntry): Promise<void> {
        if (this.isSendingError) return;
        this.isSendingError = true;
        try {
            const options = await firstValueFrom(this.dataService.httpOptions(true));

            const headers = await firstValueFrom(this.dataService.httpOptions(true));
            await firstValueFrom(this.http.post(
                this.backendLogUrl, logs, options)
            );

            this.consoleService.debug(
                `Log enviado al backend (${logs.level}): ${logs.message}`,
                logs,
                '$LlmLoggingService $sendLogToServer'
            );
        } catch (err) {
            console.error('CRITICAL: Falló el envío del log de error al backend', err, logs);
            this.consoleService.error(
                `Error al enviar log al backend`,
                { logs, error: err },
                '$LlmLoggingService $sendLogToServer'
            );
        } finally {
            this.isSendingError = false;
        }
    }

    /**
     * Inicia el procesador de lotes
     */
    private startBatchProcessor(): void {
        this.batchTimer = setInterval(() => {
            this.flushLogs();
        }, this.BATCH_INTERVAL);
    }

    /**
     * Inicializa el tracking de sesión
     */
    private initializeSessionTracking(): void {
        // Trackea cambios de ruta
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.sessionInfo.previousRoute = this.sessionInfo.currentRoute;
                this.sessionInfo.currentRoute = event.url;
                this.sessionInfo.pageVisits = (this.sessionInfo.pageVisits || 0) + 1;
                this.updateLastActivity();
            }
        });

        // Trackea actividad del usuario
        ['click', 'keypress', 'scroll', 'mousemove'].forEach(eventType => {
            window.addEventListener(eventType, () => this.updateLastActivity(), { passive: true });
        });

        // Envía logs pendientes antes de cerrar
        window.addEventListener('beforeunload', () => {
            this.flushLogs();
        });
    }

    /**
     * Actualiza el timestamp de última actividad
     */
    private updateLastActivity(): void {
        this.sessionInfo.lastActivity = new Date().toISOString();
    }

    /**
     * Genera un ID único de sesión
     */
    private generateSessionId(): string {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Limpia recursos al destruir el servicio
     */
    ngOnDestroy(): void {
        if (this.batchTimer) {
            clearInterval(this.batchTimer);
        }
        this.flushLogs();
    }
}
